import { useEffect, useMemo, useState } from 'react';
import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { cn } from 'common/utils';
import JobContent from 'components/v2/modules/careers-page/job-content';
import { JobType } from 'service/careers';

import styles from './careers-content.module.scss';

const CareersPageContent = ({ jobs }: { jobs: JobType[] }) => {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [currentFilter, setCurrentFilter] = useState<string>('All Positions');
  const [isLoaded, setIsLoaded] = useState(false);
  const filterOptions = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(
        jobs.flatMap((job) => job.position_type).filter((type) => type !== ''),
      ),
    );
    return ['All Positions', ...uniqueTypes];
  }, [jobs]);
  const filterJobs = useMemo(() => {
    if (currentFilter === 'All Positions') {
      return jobs;
    }
    return jobs.filter((job) => {
      return job.position_type.includes(currentFilter);
    });
  }, [jobs, currentFilter]);

  const toggleJobExpansion = (jobId: string) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes slideUpFromBottom {
          from {
            opacity: 0;
            transform: translateY(60px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-title {
          opacity: 1;
          transform: translateY(0);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .hero-title:not(.loaded) {
          opacity: 0;
          transform: translateY(60px);
        }

        .hero-description {
          opacity: 1;
          transform: translateY(0);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: 0.2s;
        }

        .hero-description:not(.loaded) {
          opacity: 0;
          transform: translateY(60px);
        }

        .values-header-title {
          opacity: 1;
          transform: translateY(0);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: 0.4s;
        }

        .values-header-title:not(.loaded) {
          opacity: 0;
          transform: translateY(60px);
        }

        .values-header-subtitle {
          opacity: 1;
          transform: translateY(0);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: 0.6s;
        }

        .values-header-subtitle:not(.loaded) {
          opacity: 0;
          transform: translateY(60px);
        }
      `}</style>
      <div className="bg-white">
        {/* Hero Section with Financial Chart Background */}
        <section className="relative flex min-h-[60vh] items-center justify-center overflow-hidden p-12">
          {/* Overlay để làm mờ background */}
          <div
            className="absolute inset-0 size-full opacity-30"
            style={{
              filter: 'blur(0.5px)',
              backgroundImage: 'url(/images/about/about-careers-bg.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
          {/* Hero Content */}
          <div className="relative z-10 mx-auto flex max-w-5xl items-center justify-center">
            <div className="max-w-3xl rounded-2xl bg-white p-10 shadow-xl">
              <h1
                className={`hero-title ${isLoaded ? 'loaded' : ''} my-0 text-4xl font-black leading-[1.1] tracking-tight text-pp-text-primary md:text-6xl md:leading-[1.1]`}
              >
                Build. Invest. Grow.
                <br />
                With{' '}
                <span className="animate-pulse bg-gradient-to-r from-pp-green-primary to-green-500 bg-clip-text text-transparent">
                  PostPrime
                </span>
              </h1>
              <p
                className={`hero-description ${isLoaded ? 'loaded' : ''} mt-8 text-xl leading-relaxed text-pp-text-secondary md:text-[22px]`}
              >
                PostPrimeでは、人々が学び、投資し、共に成長する新たな場を提供しています。
                <br />
                あなたのアイデアは世界中の何百万もの人々に届くソリューションに。私たちと共に成長していきませんか。
              </p>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="relative overflow-hidden px-6 py-20 md:px-10 md:py-32">
          <div className="relative z-10 mx-auto max-w-screen-xl">
            {/* Header */}
            <div className="relative mb-20 text-center">
              <h2
                className={`values-header-title ${isLoaded ? 'loaded' : ''} mb-6 text-5xl font-black lg:text-7xl`}
                style={{
                  letterSpacing: '-3px',
                  background:
                    'linear-gradient(135deg, #262626 0%, #65676B 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Our Core Values
              </h2>
              <p
                className={`values-header-subtitle ${isLoaded ? 'loaded' : ''} mx-auto text-xl font-light text-pp-text-secondary md:text-2xl`}
              >
                私たちが大切にしている行動指針
              </p>
            </div>
            {/* Grid */}
            <div
              className={cn(
                styles['values-grid'],
                'grid grid-cols-1 gap-6 md:grid-cols-12 md:grid-rows-2',
              )}
            >
              {/* Communication - Large Left */}
              <div
                className="value-card group relative col-span-1 row-span-1 flex cursor-default flex-col overflow-hidden rounded-3xl border border-gray-200 p-6 opacity-0 hover:-translate-y-2 hover:border-gray-300 hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)] md:col-span-6 lg:p-8 xl:p-12"
                style={{
                  background:
                    'linear-gradient(135deg, #FFFFFF 0%, #F8FFF9 100%)',
                  transition:
                    'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  animation: 'fadeInScale 0.8s ease forwards',
                  animationDelay: '0.1s',
                }}
              >
                <div
                  className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      'linear-gradient(135deg, transparent 30%, rgba(0, 200, 83, 0.02) 100%)',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className={cn(
                    styles['value-icon'],
                    styles['icon-communication'],
                    'group-hover:rotate-[10deg] group-hover:scale-110 group-hover:bg-green-200 group-hover:before:bg-growth-green',
                  )}
                ></div>
                <div className="absolute right-12 top-8 text-[120px] font-black text-gray-100 transition-all duration-300 group-hover:scale-110 group-hover:text-green-200">
                  01
                </div>
                <div className="relative z-10">
                  <h3 className="mb-4 text-[32px] font-extrabold text-pp-text-primary">
                    コミュニケーション
                  </h3>
                  <p className="mb-5 text-lg font-medium leading-tight text-pp-text-primary">
                    クリエイターのように発想し、共有する力
                  </p>
                  <p className="mb-5 text-base leading-relaxed text-pp-text-secondary">
                    PostPrimeでは、すべてのメンバーがクリエイターであると考えています。コミュニケーションとは、単に明確に伝えることに留まらず、創造性と共感をもってアイデアを磨き上げ、仲間やコミュニティに響く形で共有することを意味します。
                  </p>
                </div>
              </div>

              {/* Creativity - Large Right */}
              <div
                className="value-card group relative col-span-1 row-span-1 flex cursor-default flex-col overflow-hidden rounded-3xl border border-gray-200 p-6 opacity-0 hover:-translate-y-2 hover:border-gray-300 hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)] md:col-span-6 lg:p-8 xl:p-12"
                style={{
                  background:
                    'linear-gradient(135deg, #FFFFFF 0%, #FFF8F8 100%)',
                  transition:
                    'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  animation: 'fadeInScale 0.8s ease forwards',
                  animationDelay: '0.2s',
                }}
              >
                <div
                  className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      'linear-gradient(135deg, transparent 30%, rgba(0, 200, 83, 0.02) 100%)',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className={cn(
                    styles['value-icon'],
                    styles['icon-creativity'],
                    'group-hover:before:rotate-135 group-hover:rotate-[10deg] group-hover:scale-110 group-hover:bg-green-200 group-hover:before:bg-growth-green group-hover:after:border-growth-green',
                  )}
                ></div>
                <div className="absolute right-12 top-8 text-[120px] font-black text-gray-100 transition-all duration-300 group-hover:scale-110 group-hover:text-green-200">
                  02
                </div>
                <div className="relative z-10">
                  <h3 className="mb-4 text-[32px] font-extrabold text-pp-text-primary">
                    創造性
                  </h3>
                  <p className="mb-5 text-lg font-medium leading-tight text-pp-text-primary">
                    新しい発想やアイデアを尊重する姿勢
                  </p>
                  <p className="mb-5 text-base leading-relaxed text-pp-text-secondary">
                    革新は、多様な視点や発想を尊重する文化から生まれます。PostPrimeでは、自由な探求や挑戦を歓迎し、少し大胆なアイデアでも価値のある成果につなげていくことを大切にしております。
                  </p>
                </div>
              </div>

              {/* Teamwork - Medium Left */}
              <div
                className="value-card group relative col-span-1 row-span-1 flex cursor-default flex-col overflow-hidden rounded-3xl border border-gray-200 p-6 opacity-0 hover:-translate-y-2 hover:border-gray-300 hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)] md:col-span-5 lg:p-8 xl:p-12"
                style={{
                  background:
                    'linear-gradient(135deg, #FFFFFF 0%, #F8F9FF 100%)',
                  transition:
                    'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  animation: 'fadeInScale 0.8s ease forwards',
                  animationDelay: '0.3s',
                }}
              >
                <div
                  className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      'linear-gradient(135deg, transparent 30%, rgba(0, 200, 83, 0.02) 100%)',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className={cn(
                    styles['value-icon'],
                    styles['icon-teamwork'],
                    'group-hover:after:box-shadow-[-12px_8px_0_#00c853,12px_8px_0_#00c853] group-hover:rotate-[10deg] group-hover:scale-110 group-hover:bg-green-200 group-hover:before:bg-growth-green group-hover:before:shadow-[-12px_8px_0_#00c853,12px_8px_0_#00c853] group-hover:after:bg-growth-green',
                  )}
                ></div>
                <div className="absolute right-12 top-8 text-[120px] font-black text-gray-100 transition-all duration-300 group-hover:scale-110 group-hover:text-green-200">
                  03
                </div>
                <div className="relative z-10">
                  <h3 className="mb-4 text-[32px] font-extrabold text-pp-text-primary">
                    チームワーク
                  </h3>
                  <p className="mb-5 text-lg font-medium leading-tight text-pp-text-primary">
                    状況に関わらず、仲間を支え合う姿勢
                  </p>
                  <p className="mb-5 text-base leading-relaxed text-pp-text-secondary">
                    私たちは、互いを支え合うことで組織としての力を高めています。チームワークとは、仲間が困難な状況にあるときに助け合い、成果を共に喜び合い、あらゆる状況下で協力しながら最適な解決策を見出す姿勢を指します。
                  </p>
                </div>
              </div>

              {/* Responsibility - Large Right */}
              <div
                className="value-card group relative col-span-1 row-span-1 flex cursor-default flex-col overflow-hidden rounded-3xl border border-gray-200 p-6 opacity-0 hover:-translate-y-2 hover:border-gray-300 hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)] md:col-span-7 lg:p-8 xl:p-12"
                style={{
                  background:
                    'linear-gradient(135deg, #FFFFFF 0%, #FFFEF8 100%)',
                  transition:
                    'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  animation: 'fadeInScale 0.8s ease forwards',
                  animationDelay: '0.4s',
                }}
              >
                <div
                  className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      'linear-gradient(135deg, transparent 30%, rgba(0, 200, 83, 0.02) 100%)',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className={cn(
                    styles['value-icon'],
                    styles['icon-responsibility'],
                    'group-hover:rotate-[10deg] group-hover:scale-110 group-hover:bg-green-200 group-hover:before:border-growth-green group-hover:after:border-growth-green',
                  )}
                ></div>
                <div className="absolute right-12 top-8 text-[120px] font-black text-gray-100 transition-all duration-300 group-hover:scale-110 group-hover:text-green-200">
                  04
                </div>
                <div className="relative z-10">
                  <h3 className="mb-4 text-[32px] font-extrabold text-pp-text-primary">
                    責任感
                  </h3>
                  <p className="mb-5 text-lg font-medium leading-tight text-pp-text-primary">
                    一つひとつのプロジェクトに主体的に取り組む姿勢
                  </p>
                  <p className="mb-5 text-base leading-relaxed text-pp-text-secondary">
                    PostPrimeにおける責任感は、単なる納期遵守にとどまりません。成果を自らの責任として受け止め、適切な判断を行い、自分の仕事がチームや会社の目標達成に貢献することを意識して取り組むことを重視します。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions Section */}
        <section className="bg-gray-50 py-16">
          <div className="mx-auto max-w-screen-xl px-6 lg:px-10">
            {/* Filter Section */}
            <div className="mb-8">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-[32px] font-bold tracking-tight text-pp-text-primary">
                  Open Positions
                </h2>
                <span className="text-base text-pp-text-secondary">
                  {filterJobs.length} positions available
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                {filterOptions.map((filter, index) => (
                  <button
                    key={`${filter}-${index}`}
                    onClick={() => setCurrentFilter(filter)}
                    className={`flex items-center justify-center rounded-full border border-solid px-4 py-2 text-sm font-normal transition-colors ${
                      currentFilter === filter
                        ? 'border-growth-green bg-pp-green-subtle text-growth-green'
                        : 'border-pp-border-primary bg-white text-pp-text-secondary hover:border-line-default hover:bg-pp-bg-secondary'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            {/* Jobs Grid */}
            <div className="space-y-6">
              {filterJobs.map((job) => (
                <JobContent
                  key={job.id}
                  job={job}
                  expandedJob={expandedJob}
                  onToggleExpansion={toggleJobExpansion}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-white py-16">
          <div className="mx-auto max-w-screen-xl px-6 text-center lg:px-10">
            <h2 className="mb-4 text-[36px] font-bold tracking-tight text-pp-text-primary">
              オープンポジション
            </h2>
            <p className="mb-8 text-lg text-pp-text-secondary">
              どの職種に応募すべきか迷っている方や、ご自身の経験・スキルを幅広く活かせる可能性を探したい方はこちらからご応募ください。
              <br />
              ※ご登録いただいた内容をもとに、
              <strong>
                ご経験に合うポジションが確認できた際にご連絡いたします。
              </strong>
            </p>
            <a
              href="https://forms.gle/KLLicf3iiLYaF7n76"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-growth-green px-6 py-3 text-base font-medium text-white transition-colors hover:bg-green-700"
            >
              Get in Touch
              <ExternalLinkIcon className="size-5" />
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default CareersPageContent;
